<template>
  <div class="asc-scc-details-table">
    <div class="asc-scc-details-table--header">
      <div class="asc-scc-details-table--header-select-unselect">
        <asc-list-filter-button
          v-model="listFilterStatus"
        ></asc-list-filter-button>
      </div>
      <div class="asc-scc-details-table--header-collapse-expand">
        <asc-accent-button
          :button-text="getExpandCollapseText"
          :on-click="handleTableExpandCollapse"
          :icon="getExpandCollapseIcon"
          secondary
          nohover
        ></asc-accent-button>
      </div>
    </div>
    <div v-if="hasGroups && isItemGrouped" class="asc-scc-details-table--data">
      <div
        v-for="(group, groupIndex) in getGroups"
        :key="`group_${groupIndex}`"
        class="asc-scc-details-table--selectionsGroup"
      >
        <div class="asc-scc-details-table--selectionsGroup-name">
          {{ group.name }}
        </div>
        <asc-subscription-center-details-table-item
          v-for="(item, index) in getItemsInThisGroup(group)"
          :key="index"
          :item="item"
          :groups="groups"
          :selection-label="selectionLabel"
          v-on="$listeners"
        />
      </div>
    </div>
    <div v-else class="asc-scc-details-table--data">
      <asc-subscription-center-details-table-item
        v-for="(item, index) in getTableItems"
        :key="index"
        :item="item"
        :groups="groups"
        :selection-label="selectionLabel"
        v-on="$listeners"
      />
    </div>
  </div>
</template>

<script>
import AscAccentButton from '@/components/atoms/AscAccentButton'
import AscListFilterButton from '@/components/atoms/AscListFilterButton'
import AscSubscriptionCenterDetailsTableItem from './AscSubscriptionCenterDetailsTableItem'

export default {
  name: 'AscSubscriptionCenterDetailsTable',

  components: {
    AscAccentButton,
    AscListFilterButton,
    AscSubscriptionCenterDetailsTableItem,
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    groups: {
      type: Object,
      default: () => ({
        hasGroups: false,
      }),
    },
    selectionLabel: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isExpanded: undefined,
      listFilterStatus: this.getListFilterStatus(this.items),
      tableItems: this.items,
    }
  },

  computed: {
    getTableItems() {
      return this.tableItems
    },

    getExpandCollapseText() {
      return this.isExpanded ? 'Collapse All' : 'Expand All'
    },

    getExpandCollapseIcon() {
      return this.isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
    },

    hasGroups() {
      return this.groups.hasGroups
    },

    getGroups() {
      return this.groups.groups
    },

    isItemGrouped() {
      return Object.prototype.hasOwnProperty.call(this.tableItems[0], 'group')
    },
  },

  watch: {
    items: {
      deep: true,
      immediate: true,
      handler(newItems) {
        this.listFilterStatus = this.getListFilterStatus(newItems)
        this.tableItems = newItems
        this.handleTableExpandCollapse()
      },
    },
    // this determines if the grouping changed.  If so
    // reset the expand/collapse
    selectionLabel(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.handleTableExpandCollapse(true)
      }
    },

    listFilterStatus(newValue) {
      if (newValue === 0) {
        this.handleClear()
      }
      if (newValue === 1) {
        this.handleSelectAll()
      }
    },
  },

  mounted() {
    this.handleIndeterminate()
  },

  methods: {
    getItemsInThisGroup(group) {
      return this.tableItems.filter(item => item.group === group.value)
    },
    // we have this parameter (which is true ONLY when the collapse button is clicked
    // or it should be manually reset. This keeps it from getting called whenever a single item changes)
    handleTableExpandCollapse(fromEvent) {
      if (fromEvent) {
        this.isExpanded = !this.isExpanded
        this.$emit('onHeaderExpandAll', this.isExpanded)
      }
    },

    handleIndeterminate() {
      this.listFilterStatus = this.getListFilterStatus(this.tableItems)
    },

    handleClear() {
      this.$emit('onHeaderSelecUnselectAll', false)
    },

    handleSelectAll() {
      this.$emit('onHeaderSelecUnselectAll', true)
    },

    isEverythingChecked(items) {
      return (
        items.filter(item => {
          const numberUncheckedItems = item.items.filter(
            ti => !ti.checked,
          ).length
          return !item.checked || numberUncheckedItems > 0
        }).length === 0
      )
    },

    isEverythingUnchecked(items) {
      return (
        items.filter(item => {
          const numberCheckedItems = item.items.filter(ti => ti.checked).length
          return item.checked || numberCheckedItems > 0
        }).length === 0
      )
    },

    getListFilterStatus(items) {
      if (
        !this.isEverythingChecked(items) &&
        !this.isEverythingUnchecked(items)
      ) {
        return 2
      }
      if (this.isEverythingUnchecked(items)) {
        return 0
      }
      if (this.isEverythingChecked(items)) {
        return 1
      }
      return 0
    },
  },
}
</script>

<style lang="scss">
.asc-scc-details-table {
  &--header {
    height: 38px;
    background-color: $gray-300;
    display: flex;
    align-items: center;

    &-select-unselect {
      flex: 1 1 auto;

      .asc-list-filter-button--options-container {
        background-color: inherit;
        margin-left: 15px;

        .pretty.p-smooth label::before {
          border-color: $gray-600;
        }
      }
    }

    &-collapse-expand {
      flex: 0 0 135px;

      .asc-accent-button--accent.v-btn {
        background-color: inherit;
        border: 0;

        .v-btn__content {
          text-transform: none;
          font-weight: normal;
        }

        .v-icon {
          font-size: 20px !important;
        }
      }
    }
  }

  &--selectionsGroup {
    &-name {
      padding: 10px 17px 10px 17px;
      border: 1px solid $gray-400;
      border-width: 1px 0 1px 0;
      color: $gray-700;
      font-size: 13px;
      letter-spacing: 0.1px;
      line-height: 17px;
      text-transform: uppercase;
      height: 38px;
    }
  }

  &--data {
    overflow-y: auto;
  }

  .asc-scc-details-table--data {
    border: 1px solid $gray-400;
    max-height: 337px;
    overflow-y: scroll;
  }

  @media only screen and (max-width: 780px) {
    .asc-global-filter-table-item--header {
      label {
        max-width: 150px !important;
        width: 150px !important;
      }
    }

    .asc-global-filter-table-items--details-header {
      max-width: 200px;
    }
  }
}
</style>
